// zr-core/videoEffects — pure descriptors for camera video effects ("Add a filter or
// background during a video call"). The PURE part lives here: the effect model, validation,
// and the canvas filter string each effect maps to. The actual per-frame compositing
// (canvas + captureStream) is a thin platform pipeline in the web adapter, driven by
// `cssFilterFor`. Keeping the mapping here means the engine, demo, and codegen agree on
// exactly what each effect does and it is unit-testable with zero DOM.

export type VideoEffectType =
  | 'none'
  | 'blur' // privacy blur of the whole frame
  | 'grayscale'
  | 'sepia'
  | 'vivid' // saturation + contrast boost ("vivid" look)
  | 'cool' // cool color cast
  | 'warm' // warm color cast
  | 'background'; // replace/blur background — needs a segmenter (see segmenter hook)

export interface VideoEffect {
  type: VideoEffectType;
  /** 0..1 strength for blur/background-blur; ignored by fixed filters. */
  intensity?: number;
  /** For type 'background': an image URL or 'blur' to blur the background instead. */
  background?: string | 'blur';
}

/** The curated effect tray surfaced in the demo/codegen — id + human label. */
export const VIDEO_EFFECT_PRESETS: { type: VideoEffectType; label: string }[] = [
  { type: 'none', label: 'None' },
  { type: 'blur', label: 'Blur' },
  { type: 'grayscale', label: 'Mono' },
  { type: 'sepia', label: 'Sepia' },
  { type: 'vivid', label: 'Vivid' },
  { type: 'cool', label: 'Cool' },
  { type: 'warm', label: 'Warm' },
  { type: 'background', label: 'Background' },
];

const FIXED_FILTERS: Partial<Record<VideoEffectType, string>> = {
  none: 'none',
  grayscale: 'grayscale(1)',
  sepia: 'sepia(0.85)',
  vivid: 'saturate(1.6) contrast(1.15)',
  cool: 'saturate(1.1) hue-rotate(330deg) brightness(1.03)',
  warm: 'saturate(1.15) hue-rotate(20deg) brightness(1.03)',
};

function clamp01(n: number | undefined, fallback: number): number {
  if (n === undefined || Number.isNaN(n)) return fallback;
  return Math.max(0, Math.min(1, n));
}

/**
 * The CSS/canvas `filter` string a 2D context should apply for this effect. Used both by
 * the canvas pipeline (ctx.filter) and as a CSS preview on the local tile. For 'blur' the
 * intensity (0..1) maps to 0–18px gaussian blur. 'background' with `background:'blur'` and
 * no segmenter falls back to a full-frame blur so the effect still does *something* useful.
 */
export function cssFilterFor(effect: VideoEffect): string {
  const fixed = FIXED_FILTERS[effect.type];
  if (fixed) return fixed;
  if (effect.type === 'blur') {
    const px = Math.round(clamp01(effect.intensity, 0.5) * 18);
    return `blur(${px}px)`;
  }
  if (effect.type === 'background') {
    if (effect.background === 'blur' || !effect.background) {
      const px = Math.round(clamp01(effect.intensity, 0.6) * 16);
      return `blur(${px}px)`;
    }
    return 'none'; // image replacement is composited by the segmenter, not a CSS filter
  }
  return 'none';
}

/** True when the effect changes nothing (lets the pipeline shortcut to the raw track). */
export function isNoOpEffect(effect: VideoEffect | undefined): boolean {
  return !effect || effect.type === 'none';
}

/** True when the effect needs a background segmentation model to be fully realized. */
export function needsSegmenter(effect: VideoEffect | undefined): boolean {
  return !!effect && effect.type === 'background' && effect.background !== 'blur';
}

/** Normalize/validate developer input into a safe effect (defaults unknown → none). */
export function normalizeEffect(effect: VideoEffect | VideoEffectType | undefined): VideoEffect {
  if (!effect) return { type: 'none' };
  const e: VideoEffect = typeof effect === 'string' ? { type: effect } : { ...effect };
  const known = VIDEO_EFFECT_PRESETS.some((p) => p.type === e.type);
  if (!known) return { type: 'none' };
  if (e.intensity !== undefined) e.intensity = clamp01(e.intensity, 0.5);
  return e;
}
